// TelcoTrack - Modal Component
// Reusable modal dialog. Visual spec (surface, backdrop, motion) lives in
// modal.component.scss so every <app-modal> call site inherits it.
// Renders into document.body so that Leaflet (or any other library that captures
// DOM events on its containers) cannot intercept clicks inside the modal.
// The host itself stays untransformed: a transform would trap the fixed backdrop.
//
// Accessibility (P0): role="dialog" + aria-modal, title association (aria-labelledby),
// focus trap (Tab cycles within the dialog), initial focus on open, focus restore on close,
// Escape always closes (independent of closeOnBackdrop), and background scroll lock.
// Nested modals (Fotky → lightbox) get --modal-stack-offset so they paint above the parent;
// backdrop close requires pointerdown on the backdrop (avoids instant-close when the lightbox
// is inserted under the cursor during the same click).

import {
  Component, Input, Output, EventEmitter, HostListener,
  OnChanges, SimpleChanges, OnDestroy, AfterViewInit, ElementRef, Renderer2,
  ViewChild,
} from '@angular/core';
import { focusFirstElement, restoreFocus, trapTabKey } from '../../../utils/focus-trap.util';
import { lockBodyScroll, unlockBodyScroll } from '../../../utils/body-scroll-lock.util';
import { overlayUnmountDelayMs, prefersReducedMotion } from '../../../utils/overlay-motion.util';

let modalIdSequence = 0;
const openModalStack: ModalComponent[] = [];

/** After a modal starts leaving, map click/Escape must not dismiss the Leaflet popup. */
let mapDismissGuardUntilMs = 0;
const MAP_DISMISS_GUARD_MS = 400;

/** True while any app-modal backdrop is mounted (incl. leave) or briefly after close.
 * Map-pick suspend keeps the Spec modal mounted but must not block map pan/pick. */
export function isMapOverlayDismissBlocked(): boolean {
  if (typeof document !== 'undefined') {
    const backdrops = document.querySelectorAll('.modal-backdrop');
    for (const backdrop of Array.from(backdrops)) {
      if (backdrop.querySelector('.modal--map-pick-suspended')) continue;
      return true;
    }
  }
  return Date.now() < mapDismissGuardUntilMs;
}

function armMapDismissGuard(): void {
  mapDismissGuardUntilMs = Date.now() + MAP_DISMISS_GUARD_MS;
}

@Component({
  selector: 'app-modal',
  template: `
    <div
      *ngIf="isRendered"
      class="modal-backdrop"
      [ngClass]="backdropClass"
      [class.modal-backdrop--leaving]="isLeaving"
      (click)="onBackdropClick()"
      (mousedown)="onBackdropPointerDown($event)"
    >
      <div
        #modalDialog
        class="modal"
        [ngClass]="modalClass"
        [class.modal--leaving]="isLeaving"
        [class.modal--entering]="isEntering"
        [style.width]="width || null"
        [style.max-width]="maxWidth"
        [style.max-height]="maxHeight"
        role="dialog"
        aria-modal="true"
        tabindex="-1"
        [attr.aria-labelledby]="labelledBy"
        [attr.aria-describedby]="describedBy"
        [attr.aria-label]="!title && !subtitle && ariaLabel ? ariaLabel : null"
        (click)="$event.stopPropagation()"
        (mousedown)="onDialogPointerDown($event)"
        (keydown)="onDialogKeydown($event)"
      >
        <!-- Modal Header -->
        <div *ngIf="title || subtitle || showHeader" class="modal-header">
          <div *ngIf="title || subtitle" class="modal-heading">
            <h2 *ngIf="title" [id]="titleId" class="modal-title">{{ title }}</h2>
            <p *ngIf="subtitle" [id]="subtitleId" class="modal-subtitle">{{ subtitle }}</p>
          </div>
          <!-- Wrapper clips projected header so long titles / actions cannot paint over the close control (hit-testing was broken). -->
          <div class="modal-header__slot">
            <ng-content select="[modal-header]"></ng-content>
          </div>
          <button
            *ngIf="showCloseButton"
            type="button"
            (pointerdown)="onCloseButtonPointerDown($event)"
            (click)="onCloseButtonClick($event)"
            class="modal-close"
            aria-label="Zavřít modální okno">
            <svg class="modal-close__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        
        <!-- Modal Body: padding lives on __content, not the scrollport.
             Padding-top on an overflow scroller offsets sticky thead (top:0
             sticks below the padding) so scrolled rows peek above the header. -->
        <div class="modal-body" [style.max-height]="bodyMaxHeight">
          <div class="modal-body__content">
            <ng-content></ng-content>
          </div>
        </div>
        
        <!-- Modal Footer -->
        <div *ngIf="showFooter" class="modal-footer">
          <ng-content select="[modal-footer]"></ng-content>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./modal.component.scss'],
})
export class ModalComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() isOpen: boolean = false;
  @Input() title: string = '';
  /** Optional line under the title. Omitted when empty — existing call sites stay unchanged. */
  @Input() subtitle: string = '';
  @Input() showHeader: boolean = false;
  @Input() showFooter: boolean = false;
  @Input() showCloseButton: boolean = true;
  @Input() closeOnBackdrop: boolean = true;
  /** Accessible name fallback when there is no `title` (e.g. fully custom `[modal-header]` content). */
  @Input() ariaLabel: string = '';
  /** Explicitní šířka (např. 56rem); když je nastavená, modal má tuto šířku. */
  @Input() width: string = '';
  @Input() maxWidth: string = '90vw';
  @Input() maxHeight: string = '90vh';
  @Input() bodyMaxHeight: string = 'calc(90vh - 120px)';
  /** When false the host element stays in its original DOM position (legacy). Default true = portal to body. */
  @Input() portalToBody: boolean = true;
  /** Extra CSS class(es) applied to `.modal` (for scoped variants, e.g. poloprůhledný spec modal). */
  @Input() modalClass: string = '';
  /** Extra CSS class(es) applied to `.modal-backdrop` (pro variantu bez tmavého overlay). */
  @Input() backdropClass: string = '';

  @Output() closeEvent = new EventEmitter<void>();

  @ViewChild('modalDialog') private readonly modalDialogRef?: ElementRef<HTMLElement>;

  readonly titleId = `app-modal-title-${++modalIdSequence}`;
  readonly subtitleId = `app-modal-subtitle-${modalIdSequence}`;

  /** Title names the dialog; a subtitle alone is the name, otherwise it describes the title. */
  get labelledBy(): string | null {
    if (this.title) return this.titleId;
    if (this.subtitle) return this.subtitleId;
    return null;
  }

  get describedBy(): string | null {
    return this.title && this.subtitle ? this.subtitleId : null;
  }

  private movedToBody = false;
  private originalParent: HTMLElement | null = null;
  private originalNextSibling: Node | null = null;

  /** Element that had focus right before the modal opened — restored when it closes. */
  private elementFocusedBeforeOpen: HTMLElement | null = null;
  private scrollLockActive = false;
  private wasOpen = false;
  /** True only when pointerdown landed on the backdrop itself (not a dialog that opened under the cursor). */
  private backdropPointerDownOnSelf = false;
  /** Ignore the originating click that opened a nested modal (Fotky → lightbox). */
  private openedAtMs = 0;
  private static readonly BACKDROP_CLOSE_GRACE_MS = 400;
  /** Coalesce pointerdown + click (and late CD) so one X gesture emits closeEvent once. */
  private closeEmitPending = false;

  /** Stays true through the leave animation so the overlay is not yanked from the DOM. */
  isRendered = false;
  isLeaving = false;
  /** True while the dialog enter keyframes run (dark theme opacity override must wait). */
  isEntering = false;
  private leaveTimer: ReturnType<typeof setTimeout> | null = null;
  private leaveAnimHandler: EventListener | null = null;
  private enterTimer: ReturnType<typeof setTimeout> | null = null;
  private enterAnimHandler: EventListener | null = null;

  constructor(
    private readonly elRef: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']) {
      if (this.isOpen) {
        this.cancelLeaveWatchers();
        this.cancelEnterWatchers();
        this.isLeaving = false;
        this.closeEmitPending = false;
        this.isRendered = true;
        this.syncPortalToBody();
        this.syncOpenState();
      } else if (this.isRendered) {
        this.beginLeave();
      }
    } else if (changes['portalToBody']) {
      this.syncPortalToBody();
    }
  }

  ngAfterViewInit(): void {
    if (this.isOpen && !this.isRendered) {
      this.isRendered = true;
    }
    this.syncPortalToBody();
    this.syncOpenState();
  }

  ngOnDestroy(): void {
    this.cancelLeaveWatchers();
    this.cancelEnterWatchers();
    this.removeFromOpenStack();
    this.moveBack();
    this.releaseScrollLock();
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    // Only the topmost nested modal owns Escape; its parent popup/modal remains intact.
    // Mark the event so a lower modal does not also close when we leave the stack mid-handler.
    if (!this.isOpen || openModalStack[openModalStack.length - 1] !== this) return;
    const keyed = event as KeyboardEvent & { __appModalEscapeConsumed?: boolean };
    if (keyed.__appModalEscapeConsumed) return;
    keyed.__appModalEscapeConsumed = true;
    event.stopPropagation();
    event.preventDefault();
    this.close();
  }

  /** Traps Tab/Shift+Tab focus inside the dialog. Escape handled separately (document-level) above. */
  onDialogKeydown(event: KeyboardEvent): void {
    if (event.key === 'Tab' && this.modalDialogRef) {
      trapTabKey(event, this.modalDialogRef.nativeElement);
    }
  }

  onCloseButtonPointerDown(event: Event): void {
    // Portálovaný modal je mimo `.popup-container`. Leaflet / map CD between
    // mousedown and click can recreate popup DOM and swallow `(click)` on X
    // (same class of bug as photo thumbnails). Dismiss on pointerdown so close
    // does not depend on the click landing on the original button node.
    event.preventDefault();
    event.stopPropagation();
    this.close();
  }

  onCloseButtonClick(event: MouseEvent): void {
    // Keyboard activation (Enter) synthesizes click without pointerdown.
    event.preventDefault();
    event.stopPropagation();
    this.close();
  }

  onBackdropPointerDown(event: MouseEvent): void {
    event.stopPropagation();
    // Ignore clicks that started on a thumbnail (lightbox inserted mid-click via detectChanges).
    this.backdropPointerDownOnSelf = event.target === event.currentTarget;
  }

  onDialogPointerDown(event: MouseEvent): void {
    event.stopPropagation();
    this.backdropPointerDownOnSelf = false;
  }

  /**
   * X / Escape / backdrop always emit `closeEvent`. Call sites must dismiss
   * (`isOpen=false`) without gating on form validity — Uložit validates separately.
   */
  close(): void {
    // Ignore re-entry while leaving, and coalesce pointerdown+click (or a
    // call site that has not run CD yet) so closeEvent fires once per gesture.
    if (!this.isOpen || this.isLeaving || this.closeEmitPending) return;
    this.closeEmitPending = true;
    this.closeEvent.emit();
    // If a call site ignored closeEvent, allow another X attempt on the next turn.
    queueMicrotask(() => {
      if (this.isOpen && !this.isLeaving) this.closeEmitPending = false;
    });
  }

  onBackdropClick(): void {
    if (!this.isOpen || this.isLeaving) {
      this.backdropPointerDownOnSelf = false;
      return;
    }
    const openedMsAgo = Date.now() - this.openedAtMs;
    if (openedMsAgo < ModalComponent.BACKDROP_CLOSE_GRACE_MS) {
      this.backdropPointerDownOnSelf = false;
      return;
    }
    if (this.closeOnBackdrop && this.backdropPointerDownOnSelf) {
      this.close();
    }
    this.backdropPointerDownOnSelf = false;
  }

  /** Ensures host is under document.body when portalToBody is on (covers *ngIf + [isOpen]="true" edge cases). */
  private syncPortalToBody(): void {
    if ((this.isRendered || this.isOpen) && this.portalToBody && !this.movedToBody) {
      this.moveToBody();
    } else if (!this.isRendered && !this.isLeaving && this.movedToBody) {
      this.moveBack();
    }
  }

  /** Handles open side effects: scroll lock and initial focus. Close waits for the leave animation. */
  private syncOpenState(): void {
    if (this.isOpen && !this.wasOpen) {
      this.wasOpen = true;
      this.removeFromOpenStack();
      openModalStack.push(this);
      this.applyStackLayerOffset();
      this.backdropPointerDownOnSelf = false;
      this.openedAtMs = Date.now();
      this.elementFocusedBeforeOpen = document.activeElement as HTMLElement | null;
      this.acquireScrollLock();
      // Wait a tick so *ngIf content (and any projected content) has rendered before we query it.
      setTimeout(() => {
        if (this.isOpen && this.modalDialogRef) {
          focusFirstElement(this.modalDialogRef.nativeElement);
        }
      });
      this.syncEnterAnimation();
    }
  }

  /** Waits for dialog enter keyframes; skipped when prefers-reduced-motion. */
  private syncEnterAnimation(): void {
    this.cancelEnterWatchers();
    if (!this.isOpen || !this.isRendered || prefersReducedMotion()) {
      this.isEntering = false;
      return;
    }
    this.isEntering = true;
    setTimeout(() => {
      const dialog = this.modalDialogRef?.nativeElement;
      if (!this.isOpen || !dialog) {
        this.finishEnter();
        return;
      }
      this.enterAnimHandler = (event: Event) => {
        if (event.target !== dialog) return;
        if (!this.animationNameIncludes(event, 'tt-app-modal-enter')
          && !this.animationNameIncludes(event, 'tt-modal-dialog-enter')) return;
        this.finishEnter();
      };
      dialog.addEventListener('animationend', this.enterAnimHandler);
      this.enterTimer = setTimeout(() => this.finishEnter(), overlayUnmountDelayMs() + 50);
    });
  }

  private finishEnter(): void {
    this.cancelEnterWatchers();
    this.isEntering = false;
  }

  private cancelEnterWatchers(): void {
    if (this.enterTimer != null) {
      clearTimeout(this.enterTimer);
      this.enterTimer = null;
    }
    if (this.enterAnimHandler) {
      this.modalDialogRef?.nativeElement.removeEventListener('animationend', this.enterAnimHandler);
      this.enterAnimHandler = null;
    }
  }

  private beginLeave(): void {
    if (!this.isRendered || this.isLeaving) return;
    this.wasOpen = false;
    this.closeEmitPending = false;
    this.removeFromOpenStack();
    this.clearStackLayerOffset();
    this.backdropPointerDownOnSelf = false;
    // Leave animation keeps pointer hit-testing so a nested lightbox does not let
    // clicks fall through to the parent modal (e.g. Foto X → Fotky X). Map popup
    // dismiss stays blocked while `.modal-backdrop` is mounted + brief guard below.
    armMapDismissGuard();
    if (prefersReducedMotion()) {
      this.finishLeave();
      return;
    }
    this.isLeaving = true;
    const backdrop = this.elRef.nativeElement.querySelector('.modal-backdrop');
    if (backdrop) {
      this.leaveAnimHandler = (event: Event) => {
        if (event.target !== backdrop) return;
        // Ignore the open fadeIn completing after a quick close.
        if (!this.animationNameIncludes(event, 'tt-app-modal-fade-out')
          && !this.animationNameIncludes(event, 'tt-overlay-fade-out')) return;
        this.finishLeave();
      };
      backdrop.addEventListener('animationend', this.leaveAnimHandler);
    }
    this.leaveTimer = setTimeout(() => this.finishLeave(), overlayUnmountDelayMs() + 50);
  }

  private finishLeave(): void {
    if (!this.isRendered && !this.isLeaving) return;
    this.cancelLeaveWatchers();
    this.isLeaving = false;
    this.isRendered = false;
    this.releaseScrollLock();
    restoreFocus(this.elementFocusedBeforeOpen);
    this.elementFocusedBeforeOpen = null;
    this.syncPortalToBody();
  }

  /** Encapsulated keyframes get a component suffix; match the stable name fragment. */
  private animationNameIncludes(event: Event, fragment: string): boolean {
    const name = (event as AnimationEvent).animationName ?? '';
    return name.includes(fragment);
  }

  private cancelLeaveWatchers(): void {
    if (this.leaveTimer != null) {
      clearTimeout(this.leaveTimer);
      this.leaveTimer = null;
    }
    if (this.leaveAnimHandler) {
      const backdrop = this.elRef.nativeElement.querySelector('.modal-backdrop');
      backdrop?.removeEventListener('animationend', this.leaveAnimHandler);
      this.leaveAnimHandler = null;
    }
  }

  /** Nested lightbox (Fotky → náhled) must sit above the already-open app-modal. */
  private applyStackLayerOffset(): void {
    const index = openModalStack.lastIndexOf(this);
    const offset = Math.max(0, index) * 20;
    this.elRef.nativeElement.style.setProperty('--modal-stack-offset', String(offset));
  }

  private clearStackLayerOffset(): void {
    this.elRef.nativeElement.style.removeProperty('--modal-stack-offset');
  }

  private acquireScrollLock(): void {
    if (!this.scrollLockActive) {
      lockBodyScroll();
      this.scrollLockActive = true;
    }
  }

  private releaseScrollLock(): void {
    if (this.scrollLockActive) {
      unlockBodyScroll();
      this.scrollLockActive = false;
    }
  }

  private removeFromOpenStack(): void {
    const index = openModalStack.lastIndexOf(this);
    if (index >= 0) openModalStack.splice(index, 1);
  }

  private moveToBody(): void {
    const host = this.elRef.nativeElement;
    this.originalParent = host.parentElement;
    this.originalNextSibling = host.nextSibling;
    this.renderer.appendChild(document.body, host);
    this.movedToBody = true;
  }

  private moveBack(): void {
    if (!this.movedToBody || !this.originalParent) return;
    if (this.originalNextSibling && this.originalParent.contains(this.originalNextSibling)) {
      this.renderer.insertBefore(this.originalParent, this.elRef.nativeElement, this.originalNextSibling);
    } else {
      this.renderer.appendChild(this.originalParent, this.elRef.nativeElement);
    }
    this.movedToBody = false;
    this.originalParent = null;
    this.originalNextSibling = null;
  }
}
